import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const ACCESS_REFRESH_BUFFER_MS = 5 * 60 * 1000;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string | null> | null = null;

const clearAuthAndRedirect = () => {
  const currentPath = window.location.pathname;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');

  if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
    window.location.href = '/login';
  }
};

const getTokenExpiryMs = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload?.exp) return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
};

const isTokenExpiringSoon = (token: string | null, bufferMs: number = ACCESS_REFRESH_BUFFER_MS): boolean => {
  const expiry = getTokenExpiryMs(token);
  if (!expiry) return false;
  return Date.now() >= expiry - bufferMs;
};

export const refreshAccessToken = async (): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    clearAuthAndRedirect();
    return null;
  }

  refreshPromise = (async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refresh_token: refreshToken,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const tokenData = response.data?.data || response.data;
      const newAccessToken = tokenData?.access_token;
      const newRefreshToken = tokenData?.refresh_token;
      const user = tokenData?.user;

      if (!newAccessToken || !newRefreshToken) {
        clearAuthAndRedirect();
        return null;
      }

      localStorage.setItem('access_token', newAccessToken);
      localStorage.setItem('refresh_token', newRefreshToken);
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }

      return newAccessToken;
    } catch {
      clearAuthAndRedirect();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const scheduleTokenRefresh = (): (() => void) | undefined => {
  let timerId: number | null = null;

  const schedule = async () => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (!accessToken || !refreshToken) return;

    const expiry = getTokenExpiryMs(accessToken);
    if (!expiry) return;

    const delay = Math.max(5_000, expiry - Date.now() - ACCESS_REFRESH_BUFFER_MS);

    timerId = window.setTimeout(async () => {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        schedule();
      }
    }, delay);
  };

  schedule();

  return () => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };
};

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const isRefreshCall = config.url?.includes('/auth/refresh');

    let token = localStorage.getItem('access_token');
    if (!isRefreshCall && token && isTokenExpiringSoon(token)) {
      const refreshedToken = await refreshAccessToken();
      token = refreshedToken || token;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const isRefreshCall = originalRequest.url?.includes('/auth/refresh');
      if (isRefreshCall) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      const refreshedToken = await refreshAccessToken();

      if (refreshedToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
        return api(originalRequest);
      }
    }

    if (error.response?.status === 401) {
      clearAuthAndRedirect();
    }

    return Promise.reject(error);
  }
);

export default api;
